import { discreteAvailabilityFunctions } from "../availabilityDiscrete";
import { type IState } from "../helpers";
import { AvailabilityCalculator, type TAvailabilityFactorEntry } from "./helpers/availabilityCalculator";
import { type TComputedAvailabilityResult } from "./helpers/shared";

/**
 * CycleEnd nextAvailabilityEvent calculator.
 *
 * CycleEnd is a pure deadline rule for non-rolling cycles. The cycle has a fixed lifespan
 * of `cycleDays` calendar days from when it started. `state.cycle.days` counts calendar
 * days elapsed (incremented at every local midnight by tryStartNewDay, regardless of duty
 * status). The cycle expires when `cycle.days > cycleDays`; driving after expiry generates
 * a CycleEnd violation.
 *
 * isConsuming is always true: the deadline advances at rate −1 (one millisecond per
 * millisecond of real time) regardless of the driver's duty status. There is no status
 * that pauses the countdown. After expiry, the raw availability is negative and keeps
 * decreasing at the same rate — still rate −1.
 *
 * Two factors:
 *
 *   exhaustion (via exhaustion() → nextExhaustionEvent)
 *     The cycle expiration date — when `cycleEndRaw` crosses zero. Only fires when the
 *     cycle has not yet expired (raw availability > 0). Derived from
 *     `state.currentDate + cycleEndRaw`, a stable log-time anchor: `cycleEndRaw` changes
 *     at rate −1 and `state.currentDate` advances at the same rate (both tied to evalTime),
 *     so their sum is constant within a phase.
 *
 *   cycleRestart (via availabilityFactors() → nextAvailabilityEvent)
 *     When consecutive off-duty time reaches `cycleRestartMinimum`, `startNewCycle` fires
 *     time-based (during dummy log processing), resetting `cycle.days = 1` and jumping
 *     CycleEnd availability from its current value to approximately `cycleDays × 24h`.
 *     Uses `this.cycleResetDate(state)` — the same confirmed, stable helper used by
 *     `cycle.ts` and `dutySinceCycleRest.ts`. Returns `undefined` when: not off-duty,
 *     no `cycleRestartMinimum` configured, or the threshold has already been met (in which
 *     case `startNewCycle` already fired time-based and the cycle has already reset, so
 *     `getCycleEndAvailability` returns a positive value and we are back in the pre-expiry
 *     case).
 *
 * No `nextDayBoundary` factor is needed: the expiration date computed by
 * `getCycleEndAvailability` is stable across day boundaries. As evalTime crosses midnight,
 * `cycle.days` increments by 1 and the `(cycleDays − cycle.days)` offset decrements by 1,
 * keeping the resulting `getBeginningOfNextDay(...)` date invariant.
 */

export type CycleEndConditions = {
    cycleEndRaw: number | undefined;
};

export class CycleEndCalculator extends AvailabilityCalculator {
    private conditions(state: IState): CycleEndConditions {
        const cycleEndRaw = discreteAvailabilityFunctions.CycleEnd(state, false);
        return { cycleEndRaw };
    }

    extractConditions(state: IState): Record<string, unknown> {
        return this.conditions(state);
    }

    rawAvailability(state: IState): number | undefined {
        return this.conditions(state).cycleEndRaw;
    }

    isConsuming(_state: IState): boolean {
        // CycleEnd is a pure deadline — always consuming at rate −1.
        return true;
    }

    exhaustion(state: IState): Date | undefined {
        const { cycleEndRaw } = this.conditions(state);
        if (cycleEndRaw === undefined) return undefined;

        // Guard cycleEndRaw > 0: raw value is negative when already expired — no future
        // zero-crossing exists in that case.
        if (cycleEndRaw > 0) {
            return new Date(state.currentDate.getTime() + cycleEndRaw);
        }
        return undefined;
    }

    availabilityFactors(state: IState): Record<string, TAvailabilityFactorEntry> | undefined {
        const { cycleEndRaw } = this.conditions(state);
        if (cycleEndRaw === undefined) return undefined;

        // cycleRestart: when consecutive off-duty reaches cycleRestartMinimum, startNewCycle
        // fires time-based, resetting cycle.days = 1 and restoring CycleEnd availability to
        // approximately cycleDays × 24h — a genuine value jump inflection.
        const cycleRestartDate = this.cycleResetDate(state);
        return {
            cycleRestart: { factor: () => cycleRestartDate, label: "cycle restart" },
        };
    }
}

export const extractCycleEndConditions = (state: IState): CycleEndConditions =>
    new CycleEndCalculator().extractConditions(state) as CycleEndConditions;

export const computeCycleEndResult = (state: IState): TComputedAvailabilityResult | undefined =>
    new CycleEndCalculator().compute(state);
